import { Menu, Layout } from 'antd'
import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { NavLink, useLocation } from 'react-router-dom'
import _ from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import style from './style.module.scss'
import './index.css'
import {
  isAppAdmin,
  isAppOwner,
  isAppDeveloper,
  isAppModerator,
  checkIfFeatureRestricted,
  getFilteredExtensions,
} from '../../../../../../helpers'
import AgentConfigMenu from '../AgentConfigMenu/index'

const { Sider } = Layout

const AppNavMenu = ({
  appID,
  activeApp,
  handleFetchAnalytics,
  handleFetchUsers,
  appExpired,
  handleFetchGroups,
  handleFetchRoles,
  handleFetchChats,
  handleFetchCalls,
  handleFetchWidgets,
  handleFetchModeration,
  handleFetchCredentials,
  handleFetchSettings,
  handleFetchPlans,
  handleFetchMembers,
  handleFetchWebhooks,
  handleFetchExtensions,
  handleFetchNotificationLogs,
  handleFetchInflightMessageModeration,
  handleFetchAIAgentUserList,
  activeTab,
  plansAndBillingVisible,
  displayBanner,
}) => {
  const apps = useSelector(state => state.apps)
  const { activeBuilderAgent } = apps
  const location = useLocation()
  const [hideLegacyNotifications, setHideLegacyNotifications] = useState(false)
  const [hideLegacyModeration, setHideLegacyModeration] = useState(false)
  const [hideLegacyWidgets, setHideLegacyWidgets] = useState(false)
  // const [showAIBots, setShowAIBots] = useState(false)

  // Check if current URL contains ai-agents with agent UID
  const isAgentConfigMode =
    location.pathname.includes('/ai-agents/') && location.pathname.split('/').length > 5

  // Extract agent UID from URL when in agent config mode
  const getAgentUidFromUrl = () => {
    if (isAgentConfigMode) {
      const pathParts = location.pathname.split('/')
      return pathParts[4] // /app/{appId}/ai-agents/{agentUid}/...
    }
    return activeBuilderAgent?.uid
  }
  const inflightModeration = apps.extensionsInstalledList.find(
    extension => extension.id === 'human-moderation',
  )
  const [openKeys, setOpenKeys] = useState(() => {
    const savedKeys = localStorage.getItem('ccNavOpenKeys')
    return savedKeys ? JSON.parse(savedKeys) : []
  })

  const [selectedKeys, setSelectedKeys] = useState(() => {
    const savedSelectedKey = localStorage.getItem('ccNavSelectedKey')
    return savedSelectedKey ? [savedSelectedKey] : []
  })

  const dispatch = useDispatch()
  const userlogout = () => {
    dispatch({
      type: 'user/LOGOUT',
    })
  }

  useEffect(() => {
    const savedKeys = localStorage.getItem('ccNavOpenKeys')
    const savedSelectedKey = localStorage.getItem('ccNavSelectedKey')
    setOpenKeys(savedKeys ? JSON.parse(savedKeys) : [])
    setSelectedKeys(savedSelectedKey ? [savedSelectedKey] : [])
    const moderationSettingsTabPaths = [
      '/rules',
      '/lists',
      '/custom-categories',
      '/openai',
      '/custom-api',
      '/advanced-settings',
    ]

    // Handle agent config routes - derive selected key from path
    const agentConfigMatch = activeTab.match(/\/ai-agents\/[^/]+\/([^/]+)/)
    if (agentConfigMatch) {
      const segment = agentConfigMatch[1]
      const routeToKeyMap = {
        instructions: 'agent-instructions',
        'knowledge-base': 'agent-knowledge-base',
        tool: 'agent-tool',
        variables: 'agent-variables',
        mcp: 'agent-mcp',
        deploy: 'agent-deploy',
        logs: 'agent-logs',
        pricing: 'pricing',
      }
      const derivedKey = routeToKeyMap[segment]
      if (derivedKey) {
        setSelectedKeys([derivedKey])
        localStorage.setItem('ccNavSelectedKey', JSON.stringify(derivedKey))
        return
      }
    }

    if (activeTab.includes('moderation')) {
      const isModerationSettingsTab = moderationSettingsTabPaths.some(path =>
        activeTab.includes(path),
      )
      if (isModerationSettingsTab) {
        setOpenKeys(['moderation'])
        setSelectedKeys(['moderation-settings'])
        localStorage.setItem('ccNavOpenKeys', JSON.stringify(['moderation']))
        localStorage.setItem('ccNavSelectedKey', JSON.stringify(['moderation-settings']))
        return
      }
    }

    if (
      activeTab.includes('notifications') &&
      !activeTab.includes('get-started') &&
      !activeTab.includes('logs')
    ) {
      if (activeTab.includes(`/app/${appID}/notifications`)) {
        setOpenKeys(['Notifications'])
        setSelectedKeys(['notifications-settings'])
        localStorage.setItem('ccNavOpenKeys', JSON.stringify(['Notifications']))
        localStorage.setItem('ccNavSelectedKey', JSON.stringify(['notifications-settings']))
        return
      }
    }

    if (activeTab.includes('get-started')) {
      if (activeTab.includes('messaging-get-started')) {
        setOpenKeys(['chats'])
        setSelectedKeys(['messaging-get-started'])
        localStorage.setItem('ccNavOpenKeys', JSON.stringify(['chats']))
        localStorage.setItem('ccNavSelectedKey', JSON.stringify(['messaging-get-started']))
      } else if (activeTab.includes('calling-get-started')) {
        setOpenKeys(['calls'])
        setSelectedKeys(['calling-get-started'])
        localStorage.setItem('ccNavOpenKeys', JSON.stringify(['calls']))
        localStorage.setItem('ccNavSelectedKey', JSON.stringify(['calling-get-started']))
      } else if (activeTab.includes('moderation/get-started')) {
        setOpenKeys(['moderation'])
        setSelectedKeys(['moderation-get-started'])
        localStorage.setItem('ccNavOpenKeys', JSON.stringify(['moderation']))
        localStorage.setItem('ccNavSelectedKey', JSON.stringify(['moderation-get-started']))
      } else if (activeTab.includes('notifications-get-started')) {
        setOpenKeys(['Notifications'])
        setSelectedKeys(['notifications-get-started'])
        localStorage.setItem('ccNavOpenKeys', JSON.stringify(['Notifications']))
        localStorage.setItem('ccNavSelectedKey', JSON.stringify(['notifications-get-started']))
      } else if (activeTab.includes('ai-agent/get-started')) {
        setOpenKeys(['ai-agent'])
        setSelectedKeys(['ai-agent-get-started'])
        localStorage.setItem('ccNavOpenKeys', JSON.stringify(['ai-agent']))
        localStorage.setItem('ccNavSelectedKey', JSON.stringify(['ai-agent-get-started']))
      } else if (activeTab.includes('byo-agent/get-started')) {
        setOpenKeys(['byo-agent'])
        setSelectedKeys(['byo-agent-get-started'])
        localStorage.setItem('ccNavOpenKeys', JSON.stringify(['byo-agent']))
        localStorage.setItem('ccNavSelectedKey', JSON.stringify(['byo-agent-get-started']))
      }
    }

    if (
      activeTab.includes(`/app/${appID}/third-party-ai-agents`) ||
      activeTab.includes(`/app/${appID}/agents`) ||
      activeTab.includes('third-party-ai-agents') ||
      activeTab.includes('/agents') ||
      activeTab.includes('/ai-actions') ||
      activeTab.includes('/ai-tools')
    ) {
      setOpenKeys(['byo-agent'])
      const selected =
        activeTab.includes('third-party-ai-agents') ||
        activeTab.includes('/ai-actions') ||
        activeTab.includes('/ai-tools')
          ? 'third-party-ai-agents'
          : 'ai-agent-custom'
      setSelectedKeys([selected])
      localStorage.setItem('ccNavOpenKeys', JSON.stringify(['byo-agent']))
      localStorage.setItem('ccNavSelectedKey', JSON.stringify([selected]))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // Function to handle item selection
  const onSelect = ({ key }) => {
    if (key === 'chats-moderation') {
      setOpenKeys(['moderation'])
      setSelectedKeys(['moderation-blocked-messages'])
      localStorage.setItem('ccNavOpenKeys', JSON.stringify(['moderation']))
      localStorage.setItem('ccNavSelectedKey', JSON.stringify(['moderation-blocked-messages']))
    } else if (key === 'chats-insights') {
      setOpenKeys([])
      setSelectedKeys(['insights'])
      localStorage.setItem('ccNavOpenKeys', JSON.stringify([]))
      localStorage.setItem('ccNavSelectedKey', JSON.stringify(['insights']))
    } else if (key === 'calls-insights') {
      setOpenKeys([])
      setSelectedKeys(['insights'])
      localStorage.setItem('ccNavOpenKeys', JSON.stringify([]))
      localStorage.setItem('ccNavSelectedKey', JSON.stringify(['insights']))
    } else if (key === 'ai-chatbot-settings') {
      setOpenKeys(['chats'])
      setSelectedKeys(['chats-settings'])
      localStorage.setItem('ccNavOpenKeys', JSON.stringify(['chats']))
      localStorage.setItem('ccNavSelectedKey', JSON.stringify(['chats-settings']))
    } else {
      setSelectedKeys([key])
      localStorage.setItem('ccNavSelectedKey', [key])
    }
  }

  let siderHeight = 'calc(100vh - 150px)'
  if (displayBanner) {
    siderHeight = 'calc(100vh - 180px)'
  }

  useEffect(() => {
    const now = Math.floor(Date.now() / 1000)
    const updateVisibility = (filteredList, setHideVisibility) => {
      try {
        let counter = 0
        _.forEach(filteredList, function dc(v) {
          const installedTemp = _.findIndex(apps.extensionsInstalledList, function fn(o1) {
            return o1.id === v.id
          })
          let enabledCheckTemp = false

          if (
            apps.extensionsInstalledList[installedTemp] &&
            apps.extensionsInstalledList[installedTemp].enabled
          ) {
            enabledCheckTemp = true
          }
          if (
            !(installedTemp >= 0 && enabledCheckTemp) &&
            v.markedAsLegacyAt &&
            v.markedAsLegacyAt < now
          ) {
            counter += 1
          }
        })
        if (counter === filteredList.length) {
          setHideVisibility(true)
        } else {
          setHideVisibility(false)
        }
      } catch (error) {
        console.error('Error updating visibility of legacy menus: ', error)
      }
    }

    const filterNotificationsIds = [
      'email-notification',
      'push-notification',
      'sms-notification',
      'email-replies',
    ]
    const filteredNotifications = getFilteredExtensions(apps, filterNotificationsIds)

    updateVisibility(filteredNotifications, setHideLegacyNotifications)

    const filterModerationIds = [
      'data-masking',
      'image-moderation',
      'human-moderation',
      'profanity-filter',
      'report-message',
      'report-user',
      'sentiment-analysis',
      'slow-mode',
      'virus-malware-scanner',
      'xss-filter',
    ]
    const filteredModerations = getFilteredExtensions(apps, filterModerationIds)

    updateVisibility(filteredModerations, setHideLegacyModeration)

    setHideLegacyWidgets(checkIfFeatureRestricted(apps.active, 'extension_widget'))
  }, [apps])

  const onOpenChange = keys => {
    const latestOpenKey = keys.length ? keys[keys.length - 1] : null
    const newOpenKeys = latestOpenKey ? [latestOpenKey] : []
    setOpenKeys(newOpenKeys)
    localStorage.setItem('ccNavOpenKeys', JSON.stringify(newOpenKeys))
  }

  // Check if we should show agent config menu
  if (isAgentConfigMode) {
    return (
      <Sider
        width={208}
        className={classNames(style.siteLayoutBackground, 'appNavMenuCustom')}
        style={{ overflowY: 'scroll', height: siderHeight }}
      >
        <AgentConfigMenu
          activeTab={activeTab}
          appID={appID}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          onSelect={onSelect}
          selectedKeys={selectedKeys}
          agentUid={getAgentUidFromUrl()}
        />
      </Sider>
    )
  }

  return (
    <Sider
      width={208}
      className={classNames(style.siteLayoutBackground, 'appNavMenuCustom')}
      style={{ overflowY: 'scroll', height: siderHeight }}
    >
      <Menu
        className={style.appNavMenuCustom}
        mode="inline"
        openKeys={openKeys}
        onOpenChange={onOpenChange}
        onSelect={onSelect}
        selectedKeys={selectedKeys}
      >
        <div className={style.appNavCategoryHeader}>GENERAL</div>
        {!appExpired && (
          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              _.includes(`/app/${appID}/overview`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/overview`)
                ? style.appNavItemSelected
                : '',
            )}
            key="overview"
          >
            <NavLink
              to={`/app/${appID}/overview`}
              onClick={handleFetchAnalytics}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <div
                  className={classNames(
                    style.appNavMenuIcon,
                    style.appNavMenuIconOverview,
                    '__menu__item__icon',
                  )}
                />
                <span>Overview</span>
              </div>
            </NavLink>
          </Menu.Item>
        )}
        {/* {!appExpired && !isAppModerator(activeApp) && (
          <Menu.Item
            key="integrate"
            className={classNames(
              style.appNavMenuCustomItem,
              _.includes(`/app/${appID}/integrate`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/integrate`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink to={`/app/${appID}/integrate`} style={{ marginBottom: '0px' }} exact>
              {' '}
              <div className={style.appNavLinkWrapper}>
                <div
                  className={classNames(
                    style.appNavMenuIcon,
                    style.appNavMenuIconIntegration,
                    '__menu__item__icon',
                  )}
                />
                <span>Integrate</span>
              </div>
            </NavLink>
          </Menu.Item>
        )} */}
        {!appExpired && (
          <Menu.SubMenu
            key="manage"
            className={style.appNavMenuCustomSubMenu}
            title={
              <div className={style.appNavSubMenuCustomTitle}>
                <div
                  className={classNames(
                    style.appNavMenuIcon,
                    style.appNavMenuIconManage,
                    '__menu__item__icon',
                  )}
                />
                <span style={{ marginLeft: '8px' }}>User & Groups</span>
              </div>
            }
          >
            <Menu.Item
              key="manage-users"
              className={classNames(
                style.appNavMenuCustomItem,
                style.appNavMenuCustomItemChild,
                _.includes(`/app/${appID}/users`, activeTab) ||
                  _.includes(activeTab, `/app/${appID}/users`)
                  ? style.appNavItemSelected
                  : '',
              )}
            >
              <NavLink
                to={`/app/${appID}/users`}
                onClick={() => {
                  handleFetchUsers()
                  handleFetchRoles()
                }}
                style={{ marginBottom: '0px' }}
                exact
              >
                {' '}
                <div className={style.appNavLinkWrapper}>
                  <span>Users</span>
                </div>
              </NavLink>
            </Menu.Item>
            <Menu.Item
              key="manage-groups"
              className={classNames(
                style.appNavMenuCustomItem,
                style.appNavMenuCustomItemChild,
                _.includes(`/app/${appID}/groups`, activeTab) ||
                  _.includes(activeTab, `/app/${appID}/groups`)
                  ? style.appNavItemSelected
                  : '',
              )}
            >
              <NavLink
                to={`/app/${appID}/groups`}
                onClick={handleFetchGroups}
                style={{ marginBottom: '0px' }}
                exact
              >
                {' '}
                <div className={style.appNavLinkWrapper}>
                  <span>Groups</span>
                </div>
              </NavLink>
            </Menu.Item>
            <Menu.Item
              key="manage-users-roles"
              className={classNames(
                style.appNavMenuCustomItem,
                style.appNavMenuCustomItemChild,
                _.includes(`/app/${appID}/roles`, activeTab) ||
                  _.includes(activeTab, `/app/${appID}/roles`)
                  ? style.appNavItemSelected
                  : '',
              )}
            >
              <NavLink to={`/app/${appID}/roles`} style={{ marginBottom: '0px' }} exact>
                {' '}
                <div className={style.appNavLinkWrapper}>
                  <span>User Roles</span>
                </div>
              </NavLink>
            </Menu.Item>
          </Menu.SubMenu>
        )}
        <div className={style.appNavCategoryHeader} style={{ marginTop: '20px' }}>
          PRODUCTS
        </div>
        <Menu.SubMenu
          key="chats"
          className={style.appNavMenuCustomSubMenu}
          title={
            <div className={style.appNavSubMenuCustomTitle}>
              <div
                className={classNames(
                  style.appNavMenuIcon,
                  style.appNavMenuIconChats,
                  '__menu__item__icon',
                )}
              />
              <span style={{ marginLeft: '8px' }}>Chat & Messaging</span>
            </div>
          }
        >
          <Menu.Item
            key="messaging-get-started"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/messaging-get-started`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/messaging-get-started`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/messaging-get-started`}
              style={{ marginBottom: '0px' }}
              exact
              onClick={handleFetchModeration}
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Get Started / Integrate</span>
              </div>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            key="chats-logs"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/chats`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/chats`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/chats`}
              onClick={handleFetchChats}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Logs</span>
              </div>
            </NavLink>
          </Menu.Item>

          {/* Not nav-role-gated — matches its siblings (Logs, Moderation, Features,
              Settings); access is enforced on the page (RBAC gate in the page component).
              Gating here would hide the item while `activeApp` is still loading on refresh. */}
          <Menu.Item
            key="chats-conversation-explorer"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/conversation-explorer`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/conversation-explorer`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/conversation-explorer`}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Conversation Explorer</span>
              </div>
            </NavLink>
          </Menu.Item>

          <Menu.Item
            key="chats-features"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/chat-features`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/chat-features`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/chat-features`}
              onClick={handleFetchExtensions}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Features</span>
              </div>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            key="chats-moderation"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/moderation/flagged-messages`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/moderation/flagged-messages`) ||
                _.includes(`/app/${appID}/moderation/blocked-messages`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/moderation/blocked-messages`) ||
                _.includes(`/app/${appID}/moderation/reviewed-messages`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/moderation/reviewed-messages`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/moderation/flagged-messages`}
              onClick={handleFetchModeration}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>
                  Moderation <i className="fe fe-corner-up-right" />
                </span>
              </div>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            key="chats-insights"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/analytics`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/analytics`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink to={`/app/${appID}/analytics`} style={{ marginBottom: '0px' }} exact>
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>
                  Analytics & Insights <i className="fe fe-corner-up-right" />
                </span>
              </div>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            key="chats-settings"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/chat-settings`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/chat-settings`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/chat-settings`}
              onClick={handleFetchSettings}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Settings</span>
              </div>
            </NavLink>
          </Menu.Item>
          {!appExpired &&
            !hideLegacyWidgets &&
            (isAppDeveloper(activeApp) || isAppAdmin(activeApp) || isAppOwner(activeApp)) && (
              <Menu.Item
                key="chats-widgets"
                className={classNames(
                  style.appNavMenuCustomItem,
                  style.appNavMenuCustomItemChild,
                  _.includes(`/app/${appID}/widgets`, activeTab) ||
                    _.includes(activeTab, `/app/${appID}/widgets`)
                    ? style.appNavItemSelected
                    : '',
                )}
              >
                <NavLink
                  to={`/app/${appID}/widgets`}
                  onClick={handleFetchWidgets}
                  style={{ marginBottom: '0px' }}
                  exact
                >
                  {' '}
                  <div className={style.appNavLinkWrapper}>
                    <span>Widgets (Legacy)</span>
                  </div>
                </NavLink>
              </Menu.Item>
            )}
          {!appExpired &&
            (isAppDeveloper(activeApp) || isAppAdmin(activeApp) || isAppOwner(activeApp)) && (
              <Menu.Item
                key="chats-card-builder"
                className={classNames(
                  style.appNavMenuCustomItem,
                  style.appNavMenuCustomItemChild,
                )}
              >
                <a
                  href={`/app/${appID}/card-builder`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ marginBottom: '0px' }}
                >
                  {' '}
                  <div className={style.appNavLinkWrapper}>
                    <span>
                      Card Builder <i className="fe fe-corner-up-right" />
                    </span>
                  </div>
                </a>
              </Menu.Item>
            )}
        </Menu.SubMenu>
        <Menu.SubMenu
          key="calls"
          className={style.appNavMenuCustomSubMenu}
          title={
            <div className={style.appNavSubMenuCustomTitle}>
              <div
                className={classNames(
                  style.appNavMenuIcon,
                  style.appNavMenuIconCalls,
                  '__menu__item__icon',
                )}
              />
              <span style={{ marginLeft: '8px' }}>Voice & Video Calls</span>
            </div>
          }
        >
          <Menu.Item
            key="calling-get-started"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/calling-get-started`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/calling-get-started`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/calling-get-started`}
              style={{ marginBottom: '0px' }}
              exact
              onClick={handleFetchModeration}
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Get Started / Integrate</span>
              </div>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            key="calls-logs"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/calls`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/calls`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/calls`}
              onClick={handleFetchCalls}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Logs</span>
              </div>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            key="calls-insights"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/analytics`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/analytics`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink to={`/app/${appID}/analytics`} style={{ marginBottom: '0px' }} exact>
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>
                  Insights <i className="fe fe-corner-up-right" />
                </span>
              </div>
            </NavLink>
          </Menu.Item>
        </Menu.SubMenu>

        <Menu.SubMenu
          key="ai-agent"
          className={style.appNavMenuCustomSubMenu}
          title={
            <div className={style.appNavSubMenuCustomTitle}>
              <div
                className={classNames(
                  style.appNavMenuIcon,
                  style.appNavMenuIconAIChatBot,
                  '__menu__item__icon',
                )}
              />
              <span style={{ marginLeft: '8px' }}>AI Agents</span>
            </div>
          }
        >
          <Menu.Item
            key="ai-agent-get-started"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/ai-agent/get-started`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/ai-agent/get-started`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/ai-agent/get-started`}
              onClick={handleFetchAIAgentUserList}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Get Started / Integrate</span>
              </div>
            </NavLink>
          </Menu.Item>
          <>
            <Menu.Item
              key="ai-agents"
              className={classNames(
                style.appNavMenuCustomItem,
                style.appNavMenuCustomItemChild,
                _.includes(`/app/${appID}/ai-agents`, activeTab) ||
                  _.includes(activeTab, `/app/${appID}/ai-agents`)
                  ? style.appNavItemSelected
                  : '',
              )}
            >
              <NavLink to={`/app/${appID}/ai-agents`} style={{ marginBottom: '0px' }} exact>
                {' '}
                <div className={style.appNavLinkWrapper}>
                  <span>AI Agents</span>
                </div>
              </NavLink>
            </Menu.Item>
          </>

          {/* "Agent Builder" remains here; BYO Agents and Custom Bots moved to BYO Agents submenu */}
        </Menu.SubMenu>
        <Menu.SubMenu
          key="byo-agent"
          className={style.appNavMenuCustomSubMenu}
          title={
            <div className={style.appNavSubMenuCustomTitle}>
              <div
                className={classNames(
                  style.appNavMenuIcon,
                  style.appNavMenuIconBYOChatBot,
                  '__menu__item__icon',
                )}
              />
              <span style={{ marginLeft: '8px' }}>BYO Agents</span>
            </div>
          }
        >
          <Menu.Item
            key="byo-agent-get-started"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/byo-agent/get-started`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/byo-agent/get-started`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/byo-agent/get-started`}
              onClick={handleFetchAIAgentUserList}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Get Started / Integrate</span>
              </div>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            key="third-party-ai-agents"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/third-party-ai-agents`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/third-party-ai-agents`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/third-party-ai-agents`}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>BYO Agents</span>
              </div>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            key="ai-agent-custom"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/agents`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/agents`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink to={`/app/${appID}/agents`} style={{ marginBottom: '0px' }} exact>
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Custom Bots</span>
              </div>
            </NavLink>
          </Menu.Item>
          {/* Use existing BYO Agents tabs (Actions/Tools) elsewhere; no duplicate items here */}
        </Menu.SubMenu>

        <Menu.Item
          key="campaigns"
          className={style.appNavMenuCustomSubMenu}
          style={{ paddingLeft: '0px', marginLeft: '0px' }}
          onClick={() => {
            sessionStorage.setItem(
              'campaignsAppConfig',
              JSON.stringify({
                appId: activeApp.id,
                region: activeApp.region || 'us',
                apiKey: activeApp.accessKey,
              }),
            )
            window.open(`/app/${appID}/campaigns`, '_blank')
          }}
        >
          <div className={style.appNavSubMenuCustomTitle} style={{ cursor: 'pointer' }}>
            <div
              className={classNames(
                style.appNavMenuIcon,
                style.appNavMenuIconChats,
                '__menu__item__icon',
              )}
            />
            <span style={{ marginLeft: '8px' }}>Campaigns</span>
            <i className="fe fe-external-link" style={{ marginLeft: '6px', fontSize: '12px' }} />
          </div>
        </Menu.Item>

        <div className={style.appNavCategoryHeader} style={{ marginTop: '20px' }}>
          PLATFORM FEATURES
        </div>
        <Menu.SubMenu
          key="moderation"
          className={style.appNavMenuCustomSubMenu}
          title={
            <div className={style.appNavSubMenuCustomTitle}>
              <div
                className={classNames(
                  style.appNavMenuIcon,
                  style.appNavMenuIconModeration,
                  '__menu__item__icon',
                )}
              />
              <span style={{ marginLeft: '8px' }}>Moderation</span>
            </div>
          }
        >
          <Menu.Item
            key="moderation-get-started"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/moderation/get-started`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/moderation/get-started`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/moderation/get-started`}
              style={{ marginBottom: '0px' }}
              exact
              onClick={handleFetchModeration}
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Get Started / Integrate</span>
              </div>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            key="moderation-blocked-messages"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/moderation/flagged-messages`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/moderation/flagged-messages`) ||
                _.includes(`/app/${appID}/moderation/blocked-messages`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/moderation/blocked-messages`) ||
                _.includes(`/app/${appID}/moderation/reviewed-messages`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/moderation/reviewed-messages`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/moderation/flagged-messages`}
              style={{ marginBottom: '0px' }}
              exact
              onClick={handleFetchModeration}
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Logs</span>
              </div>
            </NavLink>
          </Menu.Item>
          {inflightModeration && inflightModeration.enabled && (
            <Menu.Item
              key="moderation-in-flight-moderation"
              className={classNames(
                style.appNavMenuCustomItem,
                style.appNavMenuCustomItemChild,
                _.includes(`/app/${appID}/moderation/in-flight-moderation`, activeTab) ||
                  _.includes(activeTab, `/app/${appID}/moderation/in-flight-moderation`)
                  ? style.appNavItemSelected
                  : '',
              )}
            >
              <NavLink
                to={`/app/${appID}/moderation/in-flight-moderation`}
                onClick={handleFetchInflightMessageModeration}
                style={{ marginBottom: '0px' }}
                exact
              >
                {' '}
                <div className={style.appNavLinkWrapper}>
                  <span>In-Flight Moderation</span>
                </div>
              </NavLink>
            </Menu.Item>
          )}
          <Menu.Item
            key="moderation-settings"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/moderation/rules`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/moderation/rules`) ||
                _.includes(`/app/${appID}/moderation/lists`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/moderation/lists`) ||
                _.includes(`/app/${appID}/moderation/advanced-settings`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/moderation/advanced-settings`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/moderation/rules`}
              onClick={handleFetchModeration}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Settings</span>
              </div>
            </NavLink>
          </Menu.Item>
          {(!hideLegacyModeration || isAppModerator(activeApp)) && (
            <Menu.Item
              key="moderation-extensions"
              className={classNames(
                style.appNavMenuCustomItem,
                style.appNavMenuCustomItemChild,
                _.includes(`/app/${appID}/moderation/legacy`, activeTab) ||
                  _.includes(activeTab, `/app/${appID}/moderation/legacy`)
                  ? style.appNavItemSelected
                  : '',
              )}
            >
              <NavLink
                to={`/app/${appID}/moderation/legacy`}
                onClick={handleFetchExtensions}
                style={{ marginBottom: '0px' }}
                exact
              >
                {' '}
                <div className={style.appNavLinkWrapper}>
                  <span>Moderation (Legacy)</span>
                </div>
              </NavLink>
            </Menu.Item>
          )}
        </Menu.SubMenu>
        <Menu.SubMenu
          key="Notifications"
          className={style.appNavMenuCustomSubMenu}
          title={
            <div className={style.appNavSubMenuCustomTitle}>
              <div
                className={classNames(
                  style.appNavMenuIcon,
                  style.appNavMenuIconNotifications,
                  '__menu__item__icon',
                )}
              />
              <span style={{ marginLeft: '8px' }}>Notification Engine</span>
            </div>
          }
        >
          <Menu.Item
            key="notifications-get-started"
            className={classNames(style.appNavMenuCustomItem, style.appNavMenuCustomItemChild)}
          >
            <NavLink
              to={`/app/${appID}/notifications-get-started`}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Get Started / Integrate</span>
              </div>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            key="notifications-logs"
            className={classNames(style.appNavMenuCustomItem, style.appNavMenuCustomItemChild)}
          >
            <NavLink
              to={`/app/${appID}/notifications-logs`}
              onClick={handleFetchNotificationLogs}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Logs</span>
              </div>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            key="notifications-settings"
            className={classNames(style.appNavMenuCustomItem, style.appNavMenuCustomItemChild)}
          >
            <NavLink
              to={`/app/${appID}/notifications`}
              onClick={handleFetchExtensions}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Settings</span>
              </div>
            </NavLink>
          </Menu.Item>
          {!hideLegacyNotifications && (
            <Menu.Item
              key="notifications-legacy"
              className={classNames(style.appNavMenuCustomItem, style.appNavMenuCustomItemChild)}
            >
              <NavLink
                to={`/app/${appID}/notifications-legacy`}
                onClick={handleFetchExtensions}
                style={{ marginBottom: '0px' }}
                exact
              >
                {' '}
                <div className={style.appNavLinkWrapper}>
                  <span>Notifications (Legacy)</span>
                </div>
              </NavLink>
            </Menu.Item>
          )}
        </Menu.SubMenu>
        <Menu.Item
          key="insights"
          className={classNames(
            style.appNavMenuCustomItem,
            _.includes(`/app/${appID}/analytics`, activeTab) ||
              _.includes(activeTab, `/app/${appID}/analytics`)
              ? style.appNavItemSelected
              : '',
          )}
        >
          <NavLink to={`/app/${appID}/analytics`} style={{ marginBottom: '0px' }} exact>
            {' '}
            <div className={style.appNavLinkWrapper}>
              <div
                className={classNames(
                  style.appNavMenuIcon,
                  style.appNavMenuIconInsights,
                  '__menu__item__icon',
                )}
              />
              <span>Analytics & Insights</span>
            </div>
          </NavLink>
        </Menu.Item>

        {apps.active?.parameters?.['dashboard.customTabs']?.length > 0 && (
          <>
            <div className={style.appNavCategoryHeader} style={{ marginTop: '20px' }}>
              MORE
            </div>
            {apps.active.parameters['dashboard.customTabs'].map(tab => (
              <Menu.Item
                className={classNames(
                  style.appNavMenuCustomItem,
                  _.includes(activeTab, `/app/${appID}/custom-tabs`) &&
                    new URLSearchParams(location.search).get('id') === tab.id
                    ? style.appNavItemSelected
                    : '',
                )}
                key={`custom-tabs-${tab.id}`}
              >
                <NavLink
                  to={`/app/${appID}/custom-tabs?id=${tab.id}`}
                  style={{ marginBottom: '0px' }}
                  exact
                >
                  {' '}
                  <div className={style.appNavLinkWrapper}>
                    <div
                      className="__menu__item__icon"
                      style={{
                        marginLeft: '8px',
                        height: '20px',
                        width: '20px',
                        maskImage: `url(${tab.icon})`,
                        backgroundColor: '#a8a4ac',
                        maskRepeat: 'no-repeat',
                        maskSize: 'contain',
                        maskPosition: 'center',
                        maskType: 'alpha',
                      }}
                    />
                    <span>{tab.label}</span>
                  </div>
                </NavLink>
              </Menu.Item>
            ))}
          </>
        )}

        <div className={style.appNavCategoryHeader} style={{ marginTop: '20px' }}>
          ACCOUNT
        </div>
        <Menu.SubMenu
          key="application"
          className={style.appNavMenuCustomSubMenu}
          title={
            <div className={style.appNavSubMenuCustomTitle}>
              <div
                className={classNames(
                  style.appNavMenuIcon,
                  style.appNavMenuIconApplication,
                  '__menu__item__icon',
                )}
              />
              <span style={{ marginLeft: '8px' }}>Application</span>
            </div>
          }
        >
          <Menu.Item
            key="application-credentials"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/credentials`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/credentials`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/credentials`}
              onClick={handleFetchCredentials}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Credentials</span>
              </div>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            key="application-webhooks"
            className={classNames(style.appNavMenuCustomItem, style.appNavMenuCustomItemChild)}
          >
            <NavLink
              to={`/app/${appID}/webhooks`}
              onClick={handleFetchWebhooks}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Webhooks</span>
              </div>
            </NavLink>
          </Menu.Item>
          {!checkIfFeatureRestricted(apps.active, 'legacy_webhooks') && (
            <Menu.Item
              key="application-webhooks-legacy"
              className={classNames(style.appNavMenuCustomItem, style.appNavMenuCustomItemChild)}
            >
              <NavLink
                to={`/app/${appID}/webhooks-legacy`}
                onClick={handleFetchWebhooks}
                style={{ marginBottom: '0px' }}
                exact
              >
                {' '}
                <div className={style.appNavLinkWrapper}>
                  <span>Webhooks (Legacy)</span>
                </div>
              </NavLink>
            </Menu.Item>
          )}
          <Menu.Item
            key="application-team-members"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/members`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/members`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/members`}
              onClick={handleFetchMembers}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Team Members</span>
              </div>
            </NavLink>
          </Menu.Item>
          {plansAndBillingVisible && (
            <Menu.Item
              key="plans-billing"
              className={classNames(
                style.appNavMenuCustomItem,
                style.appNavMenuCustomItemChild,
                _.includes(`/app/${appID}/plans-billing`, activeTab) ||
                  _.includes(activeTab, `/app/${appID}/plans-billing`)
                  ? style.appNavItemSelected
                  : '',
              )}
            >
              <NavLink
                to={`/app/${appID}/plans-billing`}
                onClick={handleFetchPlans}
                style={{ marginBottom: '0px' }}
                exact
              >
                {' '}
                <div className={style.appNavLinkWrapper}>
                  <span>Plans & Billing</span>
                </div>
              </NavLink>
            </Menu.Item>
          )}
          <Menu.Item
            key="application-settings"
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              _.includes(`/app/${appID}/general`, activeTab) ||
                _.includes(activeTab, `/app/${appID}/general`)
                ? style.appNavItemSelected
                : '',
            )}
          >
            <NavLink
              to={`/app/${appID}/general`}
              onClick={handleFetchSettings}
              style={{ marginBottom: '0px' }}
              exact
            >
              {' '}
              <div className={style.appNavLinkWrapper}>
                <span>Settings</span>
              </div>
            </NavLink>
          </Menu.Item>
        </Menu.SubMenu>

        <Menu.SubMenu
          key="profile"
          popupOffset={[0, -150]}
          className={style.appNavMenuCustomSubMenu}
          title={
            <div className={style.appNavSubMenuCustomTitle}>
              <div
                className={classNames(
                  style.appNavMenuIcon,
                  style.appNavMenuIconProfile,
                  '__menu__item__icon',
                )}
              />
              <span style={{ marginLeft: '8px' }}>Profile</span>
            </div>
          }
        >
          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
              'appNavMenuCustomItemFooter',
            )}
          >
            <a
              href="https://www.cometchat.com/blog?category=Product%20Updates"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginBottom: '0px' }}
            >
              <div className={style.appNavMenuResourcesWrapper}>
                <div>What&apos;s New</div>
              </div>
            </a>
          </Menu.Item>
          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
              'appNavMenuCustomItemFooter',
            )}
          >
            <NavLink to="/settings" style={{ marginBottom: '0px' }} exact>
              {' '}
              <div className={style.appNavMenuResourcesWrapper}>
                <span>Account Settings</span>
              </div>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
              'appNavMenuCustomItemFooter',
            )}
          >
            <a
              role="button"
              tabIndex="-1"
              onKeyPress={userlogout}
              onClick={userlogout}
              style={{ marginBottom: '0px' }}
            >
              <div className={style.appNavMenuResourcesWrapper}>
                <div>Log Out</div>
              </div>
            </a>
          </Menu.Item>
        </Menu.SubMenu>
        <Menu.SubMenu
          key="resources"
          popupOffset={[0, -300]}
          className={style.appNavMenuCustomSubMenu}
          title={
            <div className={style.appNavSubMenuCustomTitle}>
              <div
                className={classNames(
                  style.appNavMenuIcon,
                  style.appNavMenuIconResources,
                  '__menu__item__icon',
                )}
              />
              <span style={{ marginLeft: '8px' }}>Resources</span>
            </div>
          }
        >
          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
              'appNavMenuCustomItemFooter',
            )}
          >
            <a
              href="https://help.cometchat.com/hc/en-us/requests/new"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginBottom: '0px' }}
            >
              <div className={style.appNavMenuResourcesWrapper}>
                <div
                  className={classNames(
                    style.appNavMenuResourcesIcon,
                    style.appNavMenuResourcesIconSupport,
                  )}
                />
                <div>Create support ticket</div>
              </div>
            </a>
          </Menu.Item>
          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
              'appNavMenuCustomItemFooter',
            )}
          >
            <a
              href="https://www.cometchat.com/docs/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginBottom: '0px' }}
            >
              <div className={style.appNavMenuResourcesWrapper}>
                <div
                  className={classNames(
                    style.appNavMenuResourcesIcon,
                    style.appNavMenuResourcesIconDocs,
                  )}
                />
                <div>Developer docs</div>
              </div>
            </a>
          </Menu.Item>
          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
              'appNavMenuCustomItemFooter',
            )}
          >
            <a
              href="https://api-explorer.cometchat.com/reference/chat-apis"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginBottom: '0px' }}
            >
              <div className={style.appNavMenuResourcesWrapper}>
                <div
                  className={classNames(
                    style.appNavMenuResourcesIcon,
                    style.appNavMenuResourcesIconAPIDocs,
                  )}
                />
                <div>API docs</div>
              </div>
            </a>
          </Menu.Item>
          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
              'appNavMenuCustomItemFooter',
            )}
          >
            <a
              href="https://github.com/cometchat"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginBottom: '0px' }}
            >
              <div className={style.appNavMenuResourcesWrapper}>
                <div
                  className={classNames(
                    style.appNavMenuResourcesIcon,
                    style.appNavMenuResourcesIconSampleApps,
                  )}
                />
                <div>Sample apps</div>
              </div>
            </a>
          </Menu.Item>
          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
              'appNavMenuCustomItemFooter',
            )}
          >
            <a
              href="https://help.cometchat.com/hc/en-us"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginBottom: '0px' }}
            >
              <div className={style.appNavMenuResourcesWrapper}>
                <div
                  className={classNames(
                    style.appNavMenuResourcesIcon,
                    style.appNavMenuResourcesIconHelpCenter,
                  )}
                />
                <div>Help center</div>
              </div>
            </a>
          </Menu.Item>
          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
              'appNavMenuCustomItemFooter',
            )}
          >
            <a
              role="button"
              tabIndex="-1"
              onKeyPress={() => {
                dispatch({
                  type: 'settings/CHANGE_SETTING',
                  payload: {
                    setting: 'visibleModalID',
                    value: 'slackConnect',
                  },
                })
              }}
              onClick={() => {
                dispatch({
                  type: 'settings/CHANGE_SETTING',
                  payload: {
                    setting: 'visibleModalID',
                    value: 'slackConnect',
                  },
                })
              }}
              style={{ marginBottom: '0px', cursor: 'pointer', display: 'block', width: '100%' }}
            >
              <div className={style.appNavMenuResourcesWrapper}>
                <div
                  className={classNames(
                    style.appNavMenuResourcesIcon,
                    style.appNavMenuResourcesIconSlack,
                  )}
                />
                <div>Slack support</div>
              </div>
            </a>
          </Menu.Item>

          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
              'appNavMenuCustomItemFooter',
            )}
          >
            <a
              href="https://community.cometchat.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginBottom: '0px' }}
            >
              <div className={style.appNavMenuResourcesWrapper}>
                <div
                  className={classNames(
                    style.appNavMenuResourcesIcon,
                    style.appNavMenuResourcesIconCommunity,
                  )}
                />
                <div>Community</div>
              </div>
            </a>
          </Menu.Item>
          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
              'appNavMenuCustomItemFooter',
            )}
          >
            <a
              href="https://www.cometchat.com/blog?category=Product%20Updates"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginBottom: '0px' }}
            >
              <div className={style.appNavMenuResourcesWrapper}>
                <div
                  className={classNames(
                    style.appNavMenuResourcesIcon,
                    style.appNavMenuResourcesIconProductUpdates,
                  )}
                />
                <div>Product updates</div>
              </div>
            </a>
          </Menu.Item>
          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
              'appNavMenuCustomItemFooter',
            )}
          >
            <a
              href="https://feedback.cometchat.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginBottom: '0px' }}
            >
              <div className={style.appNavMenuResourcesWrapper}>
                <div
                  className={classNames(
                    style.appNavMenuResourcesIcon,
                    style.appNavMenuResourcesIconProductFeedback,
                  )}
                />
                <div>Product feedback</div>
              </div>
            </a>
          </Menu.Item>
          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
              'appNavMenuCustomItemFooter',
            )}
          >
            <a
              href="https://status.cometchat.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginBottom: '0px' }}
            >
              <div className={style.appNavMenuResourcesWrapper}>
                <div
                  className={classNames(
                    style.appNavMenuResourcesIcon,
                    style.appNavMenuResourcesIconStatusPage,
                  )}
                />
                <div>Status page</div>
              </div>
            </a>
          </Menu.Item>
        </Menu.SubMenu>
      </Menu>
    </Sider>
  )
}

export default AppNavMenu
